import {useEffect, useState} from "react";
import {
    ResponsiveContainer,
    LineChart, Line,
    BarChart, Bar, Cell, ErrorBar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import ScreenshotImage from "./screenshot.png";
import {Link} from "react-router-dom";
import {Helmet} from "react-helmet";
import ChatAI from "./ChatAI";
import Loader from "./Loader";

// Fallback categorical palette (CVD-validated, blue-led). Used only when a mortgage
// has no colour chosen in the editor. Otherwise colour follows the mortgage by name.
const PALETTE = ["#2a78d6", "#1baf7a", "#eda100", "#008300", "#4a3aa7"];

// Results are computed out-of-band, so the dashboard polls /api/getuserdashboard until it's
// ready (HTTP 202 = still computing). These control how long we wait before giving up.
const POLL_INTERVAL_MS = 4000;          // gap between dashboard readiness checks
const MAX_WAIT_MS = 8 * 60 * 1000;      // stop auto-polling after 8 min, then offer manual retry
const MIN_ERROR_WAIT_MS = 10 * 1000;    // keep retrying transient errors for at least 10s before failing
const seriesColor = (i) => PALETTE[i % PALETTE.length];
const colorFor = (nameToColor, name, i) => (nameToColor && nameToColor[name]) || seriesColor(i);

const nfCompact = new Intl.NumberFormat("he-IL", {notation: "compact", maximumFractionDigits: 1});
const nfFull = new Intl.NumberFormat("he-IL", {maximumFractionDigits: 0});
const fmtShekel = (v) => `₪${nfFull.format(Math.round(v))}`;

// Merge scatter series ([{x:[...], y:[...], name}]) into unified rows keyed by x.
function mergeByX(series = []) {
    const map = new Map();
    series.forEach((s) => {
        (s.x || []).forEach((xv, i) => {
            if (!map.has(xv)) map.set(xv, {x: xv});
            map.get(xv)[s.name] = s.y[i];
        });
    });
    return Array.from(map.values()).sort((a, b) => a.x - b.x);
}

const ChartCard = ({title, children, full}) => (
    <div className={`chart-card${full ? " chart-card--full" : ""}`}>
        <h3 className="chart-card-title">{title}</h3>
        <div className="chart-card-body">{children}</div>
    </div>
);

const LineTooltip = ({active, payload, label}) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
        <div className="chart-tooltip">
            <div className="chart-tooltip-label">חודש {label}</div>
            {payload.map((p) => (
                <div className="chart-tooltip-row" key={p.name}>
                    <span className="chart-tooltip-dot" style={{background: p.color || p.stroke}}></span>
                    <span className="chart-tooltip-name">{p.name}</span>
                    <span className="chart-tooltip-value">{fmtShekel(p.value)}</span>
                </div>
            ))}
        </div>
    );
};

const BarTooltip = ({active, payload}) => {
    if (!active || !payload || payload.length === 0) return null;
    const d = payload[0].payload;
    return (
        <div className="chart-tooltip">
            <div className="chart-tooltip-label">{d.name}</div>
            <div className="chart-tooltip-row">
                <span className="chart-tooltip-name">עלות ממוצעת</span>
                <span className="chart-tooltip-value">{fmtShekel(d.avg)}</span>
            </div>
            <div className="chart-tooltip-row">
                <span className="chart-tooltip-name">טווח (מינ׳–מקס׳)</span>
                <span className="chart-tooltip-value">{fmtShekel(d.min)}–{fmtShekel(d.max)}</span>
            </div>
        </div>
    );
};

const axisTick = {fill: "#8b8b8b", fontSize: 12};
const legendStyle = {fontSize: 13, paddingTop: 8};

function PaymentLineChart({series = [], nameToColor}) {
    const rows = mergeByX(series);
    // Start the Y-axis near the data (not at 0) so the payment lines aren't squashed into a thin
    // band at the top of the chart. Pad ~8% below/above the observed range.
    const allY = series.flatMap((s) => s.y || []).filter((v) => typeof v === "number" && isFinite(v));
    let yDomain = ["auto", "auto"];
    if (allY.length) {
        const lo = Math.min(...allY), hi = Math.max(...allY);
        const pad = (hi - lo) * 0.08 || Math.max(hi * 0.02, 1);
        yDomain = [Math.max(0, Math.floor(lo - pad)), Math.ceil(hi + pad)];
    }
    return (
        <ResponsiveContainer width="100%" height={320}>
            <LineChart data={rows} margin={{top: 10, right: 12, left: 12, bottom: 4}}>
                <CartesianGrid stroke="#ececec" vertical={false}/>
                <XAxis dataKey="x" tick={axisTick} tickLine={false} axisLine={{stroke: "#dcdcdc"}}
                       tickFormatter={(v) => `${v}`} minTickGap={28}/>
                <YAxis tick={axisTick} tickLine={false} axisLine={false} width={54}
                       domain={yDomain} allowDecimals={false}
                       tickFormatter={(v) => nfCompact.format(v)}/>
                <Tooltip content={<LineTooltip/>}/>
                <Legend wrapperStyle={legendStyle}/>
                {series.map((s, i) => (
                    <Line key={s.name} type="monotone" dataKey={s.name} stroke={colorFor(nameToColor, s.name, i)}
                          strokeWidth={2} dot={false} activeDot={{r: 4}}/>
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

function CostBarChart({data = [], nameToColor}) {
    // asymmetric whisker offsets from the average bar to the min/max of the distribution
    const rows = data.map((d) => ({...d, errY: [d.avg - d.min, d.max - d.avg]}));
    // Start the Y-axis near the data (not at 0) so the cost bars aren't all squashed into a
    // similar-looking band. Domain spans the whisker range (min..max), padded ~8%.
    const allVals = data.flatMap((d) => [d.min, d.max, d.avg]).filter((v) => typeof v === "number" && isFinite(v));
    let yDomain = [0, "auto"];
    if (allVals.length) {
        const lo = Math.min(...allVals), hi = Math.max(...allVals);
        const pad = (hi - lo) * 0.08 || Math.max(hi * 0.02, 1);
        yDomain = [Math.max(0, Math.floor(lo - pad)), Math.ceil(hi + pad)];
    }
    return (
        <ResponsiveContainer width="100%" height={320}>
            <BarChart data={rows} margin={{top: 16, right: 12, left: 12, bottom: 4}}>
                <CartesianGrid stroke="#ececec" vertical={false}/>
                <XAxis dataKey="name" tick={axisTick} tickLine={false} axisLine={{stroke: "#dcdcdc"}}/>
                <YAxis tick={axisTick} tickLine={false} axisLine={false} width={54}
                       domain={yDomain} allowDecimals={false}
                       tickFormatter={(v) => nfCompact.format(v)}/>
                <Tooltip content={<BarTooltip/>} cursor={{fill: "rgba(0,0,0,0.04)"}}/>
                <Bar dataKey="avg" radius={[4, 4, 0, 0]} maxBarSize={90} isAnimationActive={true}>
                    {rows.map((d, i) => (
                        <Cell key={d.name} fill={colorFor(nameToColor, d.name, i)}/>
                    ))}
                    <ErrorBar dataKey="errY" width={8} strokeWidth={1.5} stroke="#52514e" direction="y"/>
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

function SummaryTable({table_data = [], nameToColor}) {
    const t = table_data[0];
    if (!t || !t.header) return null;
    const headers = t.header.values;
    const cols = t.cells.values; // column-major: cols[colIndex][rowIndex]
    const rowCount = cols[0] ? cols[0].length : 0;
    const fmtCell = (v, colIndex) => {
        if (colIndex === 0) return v;                 // name
        if (typeof v === "number") return nfFull.format(Math.round(v));
        return v;
    };
    return (
        <div className="summary-table-wrap">
            <table className="summary-table">
                <thead>
                <tr>
                    {headers.map((h) => <th key={h}>{h}</th>)}
                </tr>
                </thead>
                <tbody>
                {Array.from({length: rowCount}).map((_, r) => (
                    <tr key={r}>
                        {headers.map((h, c) => (
                            <td key={h} className={c === 0 ? "summary-table-name" : ""}>
                                {c === 0 && <span className="summary-swatch" style={{background: colorFor(nameToColor, cols[0][r], r)}}></span>}
                                {fmtCell(cols[c][r], c)}
                            </td>
                        ))}
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}

function DashboardPage({user, albert}) {
    const [data, setData] = useState({});
    const [nameToColor, setNameToColor] = useState({});
    const [token, setToken] = useState(null);
    // 'loading' = polling for results, 'ready' = have data, 'timeout' = gave up (retryable),
    // 'error' = terminal backend/auth failure (retryable).
    const [status, setStatus] = useState("loading");
    const [retryCount, setRetryCount] = useState(0);
    const userid = user?.id;

    useEffect(() => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        setToken(storedToken);
      }
    }, []);

    const retry = () => {
      setStatus("loading");
      setRetryCount((c) => c + 1);
    };

    useEffect(() => {
      if (!token) return;
      const auth = {"Content-Type": "application/json", Authorization: `Bearer ${token}`};

      let cancelled = false;
      let timerId;
      const startedAt = Date.now();

      // Load the user's saved mortgage-mix JSON to map each mix name -> its chosen colour.
      // Best-effort: colours silently fall back to the palette on any failure.
      const fetchColors = async () => {
        try {
          const r = await fetch("/api/getuserjson", {method: "POST", headers: auth});
          if (!r.ok) return;
          const uj = await r.json();
          const map = {};
          (uj.mortgages || []).forEach((m) => { if (m.color) map[m.name] = m.color; });
          if (!cancelled) setNameToColor(map);
        } catch (e) { /* colours are best-effort; fall back to palette */ }
      };

      // Keep polling while pending (202) or on a transient network error, until MAX_WAIT_MS.
      const pending = () => {
        if (Date.now() - startedAt < MAX_WAIT_MS) {
          timerId = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          setStatus("timeout");
        }
      };

      const poll = async () => {
        try {
          const response = await fetch("/api/getuserdashboard", {method: "GET", headers: auth});
          if (cancelled) return;
          if (response.status === 200) {
            const d = await response.json();
            if (d.status === "no_results") {
              setStatus("empty");   // saved without running a simulation -> nothing to show yet
            } else {
              setData(d);
              setStatus("ready");
            }
          } else if (response.status === 202) {
            pending();  // still computing
          } else if (Date.now() - startedAt < MIN_ERROR_WAIT_MS) {
            // 4xx/5xx early on is often transient (cold start, results mid-write) — keep retrying
            // for the first ~10s before surfacing a failure, so we don't fail the report too fast.
            timerId = setTimeout(poll, POLL_INTERVAL_MS);
          } else {
            // still failing after the grace window — surface the error, let the user retry.
            setStatus("error");
            if (albert) albert("אירעה שגיאה בטעינת הדוח");
          }
        } catch (error) {
          // Network hiccup — treat as transient and keep polling until the cap.
          if (cancelled) return;
          console.error("Error fetching dashboard:", error);
          pending();
        }
      };

      fetchColors();
      poll();

      return () => { cancelled = true; clearTimeout(timerId); };
      // userid (not the user object) keeps the effect from re-running on unrelated App re-renders.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, userid, retryCount]);

    const [showmodal, setShoeModal] = useState(false);
    const showExplanations = () => setShoeModal(true);

    if (user.id === null) return (
        <>
            <Helmet>
                <title>מחשבון משכנתא | דוח תרחישים | דשבורד </title>
                <meta name="description" content="דוח תרחישים וניתוח תוצאות של המחשבון" />
            </Helmet>
            <center>
                <br/>
                <h4>דשבורד לדוגמא</h4>
                <p>להצגת התוצאות יש ליצור חשבון ולהתחבר.</p>
                <img src={ScreenshotImage} className="img-fluid mb-3 dashboardimage " alt="מחשבון" />
                <br/>
                <Link to="/login" className="align-items-center homepagebutton">כניסה</Link>
                <Link to="/register" className="align-items-center homepagebutton">הרשמה</Link>
            </center>
        </>
    );

    if (status === "loading") return (
        <Loader title="מכין/טוען את הדוח" text="זה יימשך מספר רגעים, תודה על ההמתנה"/>
    );

    if (status === "empty") return (
        <div className="dashboard-status">
            <i className="dashboard-status-icon bi bi-clipboard-x"></i>
            <h5 className="dashboard-status-title">אין תוצאות סימולציה עדיין</h5>
            <p className="dashboard-status-text">
                לא נמצאו תוצאות להצגה. חזרו לעריכת התמהילים ולחצו על "שמור וחשב תרחישים" כדי להריץ סימולציה.
            </p>
            <Link to="/simulator" className="addnew dashboard-status-btn">
                <i className="bi bi-sliders"></i> לעריכה והרצת סימולציה
            </Link>
        </div>
    );

    if (status === "timeout" || status === "error") return (
        <div className="dashboard-status">
            <i className={`dashboard-status-icon bi ${status === "timeout" ? "bi-hourglass-split" : "bi-exclamation-triangle dashboard-status-icon--error"}`}></i>
            <h5 className="dashboard-status-title">
                {status === "timeout" ? "החישוב אורך יותר מהרגיל" : "אירעה שגיאה בטעינת הדוח"}
            </h5>
            <p className="dashboard-status-text">
                {status === "timeout"
                    ? "התוצאות עדיין לא מוכנות. אפשר להמתין עוד רגע ולנסות שוב."
                    : "לא הצלחנו לטעון את הדוח. נסו שוב בעוד רגע."}
            </p>
            <button className="addnew dashboard-status-btn" onClick={retry}>
                <i className="bi bi-arrow-clockwise"></i> נסה שוב
            </button>
        </div>
    );

    return (
        <div className="graph-div">
            <Helmet>
                <title>מחשבון משכנתא | דוח תרחישים | דשבורד </title>
                <meta name="description" content="דוח תרחישים וניתוח תוצאות של המחשבון"/>
            </Helmet>
            <ChatAI outputText={data.html_text} show={showmodal} setShow={setShoeModal}/>

            <div id="margener" className="dashboard">
                <div className="dashboard-toolbar">
                    <h2 className="my-text-gradient">דוח תרחישים</h2>
                    <button className="letstalk dashboard-ai-btn" onClick={showExplanations}>
                        <i className="bi bi-stars"></i> הסבר על התוצאות
                    </button>
                </div>

                <div className="dashboard-grid">
                    <ChartCard title="החזר חודשי על פני זמן">
                        <PaymentLineChart series={data.payment_data} nameToColor={nameToColor}/>
                    </ChartCard>
                    <ChartCard title="עלות מימון ממוצעת לפי תמהיל (טווח מינימום–מקסימום)">
                        <CostBarChart data={data.cost_bar_data} nameToColor={nameToColor}/>
                    </ChartCard>
                    <div className="dashboard-summary-row">
                        <ChartCard title="טבלת סיכום">
                            <SummaryTable table_data={data.table_data} nameToColor={nameToColor}/>
                        </ChartCard>
                        <div className="chart-card irr-card">
                            <h3 className="chart-card-title">תשואה פנימית (IRR)</h3>
                            <div className="irr-body">
                                <i className="bi bi-graph-up-arrow irr-icon"></i>
                                <div className="irr-value">14.24%</div>
                                <div className="irr-note">נתון לדוגמה</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DashboardPage;
